import { formatMessage } from 'src/core/services/http.service';
import DataStore, { Query, QueryResult } from 'src/core/stores/data-store';
import FormStore from 'src/core/stores/form-store';
import { CommandResult } from 'src/core/stores/types';
import { RoleWithExpirationDate } from './role-assignments-store';
import { Country } from 'src/common/types';
import moment from 'moment';
import { AxiosError } from 'axios';

export const ORGANIZATIONS_MEMBER_LIMIT = 70;

export interface OrganizationMemberItem {
  userId: string;
  displayName: string;
  email: string;
  roles: RoleWithExpirationDate[];
  resendInvitation: boolean;
}

export enum OrganizationCreationStatus {
  Pending = 'Pending',
  Created = 'Created',
  Provisioned = 'Provisioned',
  Available = 'Available',
  Error = 'Error',
}

export interface OrganizationItem {
  id: string;
  displayName: string;
  email: string;
  address: string;
  subscriptionId: string;
  createdOn: string;
  createdById: string;
  members: OrganizationMemberItem[];
  parentOrganizationId?: string;
  parentOrganizationDisplayName?: string;
  isPartner: boolean;
  defaultLanguage: string;
  isBlocked: boolean;
  creationStatus: OrganizationCreationStatus;
  creationStatusDescription: string;
  country: Country;
  isTestingOrg: boolean;
  testingEndDate?: Date;
  twoFactorEnabled?: boolean;
}

export interface OrganizationSummary {
  id: string;
  displayName: string;
  email: string;
  address: string;
  subscriptionId: string;
  createdOn: string;
  createdById: string;
  parentOrganizationId?: string;
  parentOrganizationDisplayName?: string;
  isPartner: boolean;
  isBlocked: boolean;
  country: Country;
  isTestingOrg: boolean;
  testingEndDate?: Date;
}

export interface CreateOrganizationUser {
  userIdOrEmail: string;
  defaultRoleIds: any[];
}

export interface CreateOrganizationItem {
  displayName: string;
  email: string;
  address: string;
  defaultLanguage: string;
  parentOrganizationId: string;
  ownerUserIdOrEmail: string;
  initialUsers: CreateOrganizationUser[];
  country: Country;
  isTestingOrg: boolean;
  testingEndDate?: Date;
  ownerRolesExpirationDate: Date;
}

export interface OrganizationBackupItem {
  organizationName: string;
  organizationId: string;
  backupId: string;
  createOn: Date;
}
export const getDefaultOrganizationTestingEndDate = () => {
  const endDate = new Date();
  endDate.setTime(endDate.getTime() + 24 * 36e5);
  return moment(endDate);
};

export class OrganizationSummaryStore extends DataStore<OrganizationSummary> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/organizations`, []);
  }

  public async delete(id: string, params?: any) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.delete<any, CommandResult<OrganizationSummary>>(`${this.baseUrl}/${encodeURIComponent(id)}`, params);
      return response.data;
    });
  }

  public async getAll(): Promise<OrganizationSummary[]> {
    return (
      (await this.handleCallAsync(async () => {
        var response = await this.httpService.get<QueryResult<OrganizationSummary>>(`${this.baseUrl}?top=10000`);
        return response.data.items || [];
      })) ?? []
    );
  }

  public async organizationExists(id: string) {
    return (
      (await this.handleCallAsync(async () => {
        var response = await this.httpService.get<QueryResult<OrganizationSummary>>(`${this.baseUrl}/${id}`);
        if (response.data) {
          return true;
        }
        return false;
      })) ?? false
    );
  }
}

export class OrganizationItemStore extends FormStore<OrganizationItem, CreateOrganizationItem> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/identity/api/organizations`);
  }

  OrganizationMembersBasePath = 'users';
  RestartOrganizationCreationFlowBasePath = 'restart-creation-flow';
  ChangeBlockStateBasePath = 'change-block-state';
  sendInvitationPath = 'send-invitation';

  public async create(item: CreateOrganizationItem) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<CreateOrganizationItem, CommandResult<OrganizationItem>>(`${this.baseUrl}`, item);
      return result.data;
    });
  }

  public async switchMFA(id: string, newValue: boolean) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.put<{ twoFactorEnabled: boolean }, CommandResult<{ twoFactorEnabled: boolean }>>(
        `${this.baseUrl}/${id}/two-factor`,
        { twoFactorEnabled: newValue }
      );
      return result.data;
    });
  }

  public async getOrganizationData(id: string) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<OrganizationItem>(`${this.baseUrl}/${encodeURIComponent(id)}`);
      return response.data;
    });
  }

  public async restartOrganizationCreationFlow(id: string) {
    return await this.handleCallAsync(async () => {
      try {
        var result = await this.httpService.post<{}, CommandResult<OrganizationItem>>(
          `${this.baseUrl}/${encodeURIComponent(id)}/${this.RestartOrganizationCreationFlowBasePath}`,
          {}
        );
        return result.data;
      } catch (ex) {
        return { isSuccess: false, message: formatMessage(ex) };
      }
    });
  }

  public async addOrganizationMembers(id: string, userIdOrEmails: OrganizationMemberItem[]) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<{ members: OrganizationMemberItem[] }, CommandResult<{ members: OrganizationMemberItem[] }>>(
        `${this.baseUrl}/${encodeURIComponent(id)}/${this.OrganizationMembersBasePath}`,
        {
          members: userIdOrEmails,
        }
      );
      return result.data;
    });
  }

  public async deleteOrganizationMembers(id: string, userIds: string[]) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.delete<{ members: string[] }, CommandResult<{ members: string[] }>>(
        `${this.baseUrl}/${encodeURIComponent(id)}/${this.OrganizationMembersBasePath}`,
        {
          members: userIds,
        }
      );
      return result.data;
    });
  }

  public async changeOrganizationBlockStatus(id: string, isBlocked: boolean) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.put<{ isBlocked: boolean }, CommandResult<{ isBlocked: boolean }>>(
        `${this.baseUrl}/${encodeURIComponent(id)}/${this.ChangeBlockStateBasePath}`,
        { isBlocked }
      );
      return result.data;
    });
  }

  public async sendInvitation(id: string, userId: string) {
    return await this.handleCallAsync(async () => {
      var result = await this.httpService.post<{}, CommandResult<{}>>(
        `${this.baseUrl}/${encodeURIComponent(id)}/${this.sendInvitationPath}/${encodeURIComponent(userId)}`,
        {}
      );
      return result.data;
    });
  }

  public async checkLockedStatus(id: string) {
    return (
      (await this.handleCallAsync(async () => {
        var response = await this.httpService.get<boolean>(`${this.baseUrl}/check-locked-status/${id}`);
        return response.data;
      })) ?? false
    );
  }

  public async checkExportingStatus(id: string) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<QueryResult<any>>(`${this.baseUrl}/check-exporting-status/${id}`);
      return response.data;
    });
  }
}
export class OrganizationImportStore extends FormStore<any, any> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/export-import/api/backup`);
  }

  public async uploadOrganizationBackup(file: File) {
    return await this.handleCallAsync(async () => {
      try {
        var formData = new FormData();
        formData.append('file', file);
        var result = await this.httpService.post<FormData, any>(`${this.baseUrl}/restore`, formData, { timeout: 1000 * 60 * 10 });
        if (result.status === 200) {
          return { isSuccess: true };
        } else {
          throw new Error(result.data.error);
        }
      } catch (ex) {
        const error = ex as AxiosError<{ detail: string }>;
        return { isSuccess: false, error: error.response?.data.detail };
      }
    });
  }

  public async exportFile(organizationId: string, fileName: string) {
    return await this.handleCallAsync(async () => {
      try {
        var result = await this.httpService.post<any, { error: string }>(`${this.baseUrl}/${organizationId}`, {});
        if (result.status === 200) {
          return { isSuccess: true };
        } else {
          throw new Error(result.data.error);
        }
      } catch (ex) {
        const error = ex as AxiosError<{ detail: string }>;
        return { isSuccess: false, error: error.response?.data.detail };
      }
    });
  }
}

export const getOrganizationIdConstraints = (ids: string[]) => {
  let idConstraints = '';

  for (const [index, id] of ids.entries()) {
    idConstraints += `ids=${id}`;
    if (index < ids.length - 1) {
      idConstraints += '&';
    }
  }

  return idConstraints;
};

export class OrganizationBackupStore extends DataStore<OrganizationBackupItem> {
  constructor(baseUrl: string) {
    super(`${baseUrl}/export-import/api/backup`, []);
  }

  public async loadSelected(query: Partial<Query>, selectedIds: string[]) {
    let idConstraints = getOrganizationIdConstraints(selectedIds);

    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<QueryResult<OrganizationBackupItem>>(
        `${this.baseUrl}/azure-backups?${DataStore.buildUrl(query as Query)}&${idConstraints}`
      );
      this._state.set((s) => {
        s.items = response.data.items || [];
        s.count = response.data.count || 0;
        return s;
      });
      return true;
    });
  }

  public async load(query: Partial<Query>) {
    return await this.handleCallAsync(async () => {
      var response = await this.httpService.get<QueryResult<OrganizationBackupItem>>(
        `${this.baseUrl}/azure-backups?${DataStore.buildUrl(query as Query)}`
      );
      this._state.set((s) => {
        s.items = response.data.items || [];
        s.count = response.data.count || 0;
        return s;
      });
      return true;
    });
  }

  public async getByOrganizationId(organizationId: string) {
    return (
      (await this.handleCallAsync(async () => {
        var response = await this.httpService.get<OrganizationBackupItem[]>(`${this.baseUrl}/azure-backups/${organizationId}`);
        return response.data;
      })) ?? []
    );
  }

  public async restoreOrganization(backupId: string, organizationId: string) {
    const item = { tenantId: organizationId, backupId };
    return await this.handleCallAsync(async () => {
      try {
        await this.httpService.post<any, any>(`${this.baseUrl}/restore-from-azure`, item);
        return { isSuccess: true };
      } catch (error) {
        const err = error as AxiosError<{ detail: string }>;
        const errorMessage = err?.response?.data.detail;
        return { isSuccess: false, error: errorMessage };
      }
    });
  }

  public async generateDownloadUrl(backupId: string, organizationId: string) {
    try {
      const response = await this.httpService.get<{ status: string; token: string }>(
        `${this.baseUrl}/azure-backup-token/${organizationId}/${backupId}`
      );
      const url = response.data.token;
      return { isSuccess: true, url };
    } catch (e) {
      return { isSuccess: false, url: '' };
    }
  }
}
